import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, CheckCircle, Phone } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, testimonials, whyChooseUs, contactInfo } from '../data/mock';
import RealisationsCarousel from '../components/RealisationsCarousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const Home = () => {
  // Configuration du carrousel mobile pour les services (autoplay très lent : 6 secondes)
  const [emblaRef] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      containScroll: 'trimSnaps'
    },
    [Autoplay({ delay: 6000, stopOnInteraction: false })]
  );

  // Animation au scroll pour les témoignages
  const [visibleTestimonials, setVisibleTestimonials] = useState([]);
  const testimonialRefs = useRef([]);

  useEffect(() => {
    const observers = testimonialRefs.current.map((ref, index) => {
      if (!ref) return null;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleTestimonials((prev) => [...new Set([...prev, index])]);
              }, index * 150); // Délai progressif pour effet cascade
            }
          });
        },
        { threshold: 0.1 }
      );
      
      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  const getIcon = (iconName) => {
    const iconMap = {
      'home': LucideIcons.Home,
      'chef-hat': LucideIcons.ChefHat,
      'zap': LucideIcons.Zap,
      'droplet': LucideIcons.Droplet,
      'clock': LucideIcons.Clock,
      'paintbrush': LucideIcons.Paintbrush,
      'award': LucideIcons.Award,
      'users': LucideIcons.Users,
      'shield-check': LucideIcons.ShieldCheck,
      'file-text': LucideIcons.FileText,
      'headphones': LucideIcons.Headphones
    };
    const Icon = iconMap[iconName] || LucideIcons.Circle;
    return <Icon />;
  };

  // Helper function to get service image URL (replaces nested ternaries)
  const getServiceImageUrl = (serviceId) => {
    const imageMap = {
      1: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/slh1yjh7_2ee77800-476c-4ae8-a9da-716623f83482.png',
      2: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=600&h=400&fit=crop&q=80',
      3: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/wekk1usa_hrqzrzqrhq.png',
      4: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/gvsqrt02_01-tarif-plombier-prix-travaux-plomberie.jpg',
      5: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/l56caivu_e5f283f4-5c55-4b60-ba3f-35a038fb1523.png',
      6: 'https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/rq5c31v9_depannage-urgence.jpg'
    };
    return imageMap[serviceId] || imageMap[6];
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-0">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero-renovation-2.png" 
            alt="Rénovation avant/après" 
            className="w-full h-full object-cover"
          />
          {/* Gradient overlay pour lisibilité optimale */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-brand-hero/75 to-brand-hero/60"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Main Title */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}>
              Donnez une nouvelle vie à votre intérieur<br className="hidden md:block" />{' '}
              avec <span style={{ 
                background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 2px 4px rgba(245, 184, 0, 0.3), 0 4px 8px rgba(245, 184, 0, 0.2), 0 8px 16px rgba(245, 184, 0, 0.1)'
              }}>excellence</span>
            </h1>

            {/* Subtitle */}
            <p className="text-base md:text-lg lg:text-xl text-white/90 mb-8 leading-relaxed">
              Construction, rénovation et aménagement sur mesure.<br className="hidden md:block" />{' '}
              Plus de 15 ans d'expertise pour concrétiser vos projets.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
              >
                Demander mon devis gratuit
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/realisations" 
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold text-base md:text-lg hover:bg-brand-gold hover:border-brand-gold hover:text-gray-900 transition-all hover:shadow-xl"
              >
                Voir nos réalisations
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl py-6 px-8 border-2 border-brand-gold" style={{
              boxShadow: '0 0 20px rgba(249, 160, 27, 0.4), 0 0 40px rgba(255, 210, 48, 0.3)'
            }}>
              <div className="flex flex-wrap gap-6 md:gap-10 justify-center">
                <div className="flex flex-col items-center">
                  <div className="text-2xl md:text-3xl font-bold text-brand-gold mb-1">15+</div>
                  <div className="text-xs md:text-sm text-white/80">Années d'expérience</div>
                </div>
                <div className="h-12 w-px bg-brand-gold/30 hidden sm:block"></div>
                <div className="flex flex-col items-center">
                  <div className="text-2xl md:text-3xl font-bold text-brand-gold mb-1">30+</div>
                  <div className="text-xs md:text-sm text-white/80">Projets réalisés</div>
                </div>
                <div className="h-12 w-px bg-brand-gold/30 hidden sm:block"></div>
                <div className="flex flex-col items-center">
                  <div className="text-2xl md:text-3xl font-bold text-brand-gold mb-1">100%</div>
                  <div className="text-xs md:text-sm text-white/80">Clients satisfaits</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0066CC' }}>
              Nos <span style={{ 
                background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une gamme complète de services pour tous vos besoins en construction et rénovation
            </p>
          </div>

          {/* Carrousel mobile uniquement (< 768px) */}
          <div className="md:hidden overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 px-4">
              {services.map((service) => (
                <div 
                  key={service.id}
                  className="flex-[0_0_85%] min-w-0 group relative bg-white rounded-xl shadow-lg overflow-hidden"
                >
                  {/* Grande Image en haut (ratio 16:9) */}
                  <div className="relative w-full h-64 overflow-hidden">
                    <img 
                      src={getServiceImageUrl(service.id)}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay subtil */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    
                    {/* Badge doré avec icône en coin supérieur */}
                    <div className="absolute top-4 right-4 w-14 h-14 bg-brand-gold rounded-xl flex items-center justify-center text-white shadow-xl">
                      {getIcon(service.icon)}
                    </div>
                  </div>

                  {/* Contenu avec fond blanc pur */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                      {service.description}
                    </p>

                    {/* Liste des caractéristiques */}
                    <div className="space-y-2 mb-6">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-brand-gold mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Bouton CTA */}
                    <Link 
                      to="/contact"
                      className="inline-flex items-center justify-center w-full gap-2 bg-brand-gold text-gray-900 px-4 py-3 rounded-lg font-semibold text-sm hover:bg-brand-gold-dark transition-all hover:shadow-lg group/btn"
                    >
                      <span>Demander mon devis gratuit</span>
                      <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grid desktop/tablette (>= 768px) */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service) => (
              <div 
                key={service.id}
                className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden hover:-translate-y-3"
              >
                {/* Grande Image en haut (ratio 16:9) */}
                <div className="relative w-full h-64 overflow-hidden">
                  <img 
                    src={getServiceImageUrl(service.id)}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Overlay subtil */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  
                  {/* Badge doré avec icône en coin supérieur */}
                  <div className="absolute top-4 right-4 w-14 h-14 bg-brand-gold rounded-xl flex items-center justify-center text-white shadow-xl transform transition-transform group-hover:scale-110 group-hover:rotate-6">
                    {getIcon(service.icon)}
                  </div>
                </div>

                {/* Contenu avec fond blanc pur */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-brand-gold transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                    {service.description}
                  </p>

                  {/* Liste des caractéristiques */}
                  <div className="space-y-2 mb-6">
                    {service.features.slice(0, 4).map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-brand-gold mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bouton CTA */}
                  <Link 
                    to="/contact"
                    className="inline-flex items-center justify-center w-full gap-2 bg-brand-gold text-gray-900 px-4 py-3 rounded-lg font-semibold text-sm hover:bg-brand-gold-dark transition-all hover:shadow-lg group/btn"
                  >
                    <span>Demander mon devis gratuit</span>
                    <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 bg-brand-gold text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
            >
              Voir tous les détails de nos services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA après Services */}
      <section className="py-12 bg-gradient-to-r from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 md:p-12 border-4 border-brand-gold" style={{ boxShadow: '0 0 40px rgba(245, 184, 0, 0.5), 0 10px 30px rgba(0,0,0,0.1)' }}>
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#0066CC' }}>
                Un projet en tête ? <span style={{ 
                  background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Parlons-en</span>
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                Chaque projet est unique. Obtenez un devis personnalisé adapté à vos besoins et votre budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center gap-2 bg-brand-gold text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
                >
                  Demander mon devis gratuit
                  <ArrowRight size={20} />
                </Link>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 bg-brand-hero text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-gold hover:text-gray-900 transition-all hover:shadow-xl"
                >
                  <Phone size={20} />
                  Appelez-nous
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Option B Étoffée */}
      <section className="py-12 md:py-16" style={{
        background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
      }}>
        <div className="container mx-auto px-4">
          {/* Titre principal mis en avant */}
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
              Pourquoi <span style={{ 
                background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Nous Choisir</span>
            </h2>
          </div>
          
          {/* Grille des points clés avec descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item) => (
              <div 
                key={item.id}
                className="flex flex-col items-center text-center group hover:scale-105 transition-transform duration-300"
              >
                {/* Icône */}
                <div className="w-16 h-16 bg-brand-gold rounded-full flex items-center justify-center text-white mb-4 group-hover:shadow-xl group-hover:shadow-brand-gold/50 transition-all">
                  {getIcon(item.icon)}
                </div>
                
                {/* Titre */}
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                  {item.title}
                </h3>
                
                {/* Description courte */}
                <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Réalisations Carousel */}
      <RealisationsCarousel />

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0066CC' }}>
              Ce Que Disent <span style={{ 
                background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Nos Clients</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              La satisfaction de nos clients est notre meilleure récompense
            </p>
          </div>

          {/* Stack vertical avec animation sur mobile, grid sur desktop */}
          <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-3 md:gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                ref={(el) => (testimonialRefs.current[index] = el)}
                className={`bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-100 transition-all duration-700 ${
                  visibleTestimonials.includes(index) 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8 md:opacity-100 md:translate-y-0'
                }`}
                style={{
                  transitionDelay: `${index * 150}ms`
                }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="fill-brand-gold text-brand-gold" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-bold text-gray-800">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  <p className="text-xs text-brand-blue mt-1">{testimonial.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" style={{
        background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
      }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à Démarrer <span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Votre Projet</span> ?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Obtenez un devis gratuit et sans engagement. Notre équipe est à votre écoute pour concrétiser vos idées.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl hover:scale-105"
            >
              Demander mon devis gratuit
              <ArrowRight size={20} />
            </Link>
            <a 
              href={`tel:${contactInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-brand-blue px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              <Phone size={20} />
              {contactInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Spacer blanc */}
      <section className="bg-white py-16"></section>
    </div>
  );
};

export default Home;
